import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { KeyRound, UserRoundX, type LucideIcon } from "lucide-react";
import type { ToolboxCapability } from "@ttc-spectree/toolbox-contract/roles";
import { Button } from "@/components/ui/button";
import { useMe } from "@/hooks/queries/use-me";
import { useCanAnywhere } from "@/hooks/queries/use-toolbox-access";
import { FOGBUGZ_TOOL_NAME } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";

function Loading() {
  return (
    <div className="flex min-h-[300px] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/** One card in the page's place: icon, heading, one line and an optional action. */
function GateCard({
  testId,
  title,
  icon: Icon,
  heading,
  body,
  action,
}: {
  testId: string;
  title: string;
  icon: LucideIcon;
  heading: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col gap-4 px-4 pb-6 pt-7">
      <h1 className={TOOLBOX_PAGE_TITLE}>{title}</h1>
      <div className="glass-card flex items-start gap-3 rounded-[10px] px-[18px] py-4" data-testid={testId}>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1A1A1E]" aria-hidden="true">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </span>
        <div className="flex flex-1 flex-col gap-1">
          <h2 className="font-display text-base font-semibold leading-snug">{heading}</h2>
          <p className="leading-relaxed text-muted-foreground">{body}</p>
        </div>
        {action}
      </div>
    </div>
  );
}

/**
 * The gate of the FogBugz event log summary page (epic PI-SPEC-SW-E307,
 * ER-1663 Resolved Decision 3): the `fogbugz` Toolbox capability on at least
 * one project (an Admin everywhere) AND the person's own saved FogBugz key,
 * because the page reads and posts to FogBugz as them through the SpecTree
 * API.
 *
 * Without the capability it shows a no-role card ("Ask an Admin or your
 * Project Manager for a role"); with the capability but no key it shows
 * "Save your FogBugz key" with a Settings button to `/settings`. The SpecTree
 * API refuses the same requests, so this only spares the person a page whose
 * every action would fail. Renders in a browser and in SpecTree Toolbox.
 *
 * The FogBugz Formulator (epic PI-SPEC-SW-E309, ER-1665 Resolved Decision 3)
 * uses the same gate with its own `toolName` and `keyReason` (why it needs
 * the key), so the cards name the page the person opened.
 *
 * FogBugz to ADO (epic PI-SPEC-SW-E311, ER-1666 Package E) uses it with
 * `capability="fogbugz-to-ado"` (Admins and Project Managers) and its own
 * `noRoleBody`.
 */
export function RequireFogbugzToolbox({
  children,
  toolName = FOGBUGZ_TOOL_NAME,
  keyReason = "reads the case and posts the summary as you",
  capability = "fogbugz",
  noRoleBody = "Ask an Admin or your Project Manager for a role that includes the FogBugz tools.",
}: {
  children: ReactNode;
  /** The Toolbox capability the page needs on at least one project. */
  capability?: Extract<ToolboxCapability, "fogbugz" | "fogbugz-to-ado">;
  /** The no-role card's one line. */
  noRoleBody?: string;
  /** The page's plain name, shown as the card title. */
  toolName?: string;
  /** Why the page needs the key, after the tool name: "<toolName> <keyReason>, with your own FogBugz key." */
  keyReason?: string;
}) {
  const { data: me, isLoading: meLoading } = useMe();
  const { allowed, isLoading: accessLoading } = useCanAnywhere(capability);

  if (meLoading || accessLoading) return <Loading />;

  if (!allowed) {
    return (
      <GateCard
        testId="fogbugz-no-role"
        title={toolName}
        icon={UserRoundX}
        heading="No Toolbox role for FogBugz tools"
        body={noRoleBody}
      />
    );
  }

  if (!me?.hasFogbugzKey) {
    return (
      <GateCard
        testId="fogbugz-needs-key"
        title={toolName}
        icon={KeyRound}
        heading="Save your FogBugz key"
        body={`${toolName} ${keyReason}, with your own FogBugz key. Save it in Settings, then come back.`}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/settings">Settings</Link>
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
